import { Component } from '@theme/component';

/**
 * GOOMY · Selector de compra única vs suscripción.
 *
 * Cada tarjeta tiene un data-selling-plan (vacío = compra única). Al elegir una,
 * marcamos la activa y escribimos el valor en el input oculto name="selling_plan"
 * que vive dentro del formulario de compra, para que se mande al carrito.
 *
 * Además, escucha los cambios del selector de cantidad (evento
 * "quantity-selector:update", que burbujea hasta el formulario) y recalcula los
 * montos mostrados: precio compra única, precio suscripción, precio tachado y
 * el ahorro mensual (descuento por unidad × cantidad + ahorro de envío fijo).
 */
class GoomySubscription extends Component {
  connectedCallback() {
    super.connectedCallback?.();

    // Algunos formatos de moneda traen HTML (<span>); lo quitamos porque
    // escribimos con textContent (texto plano).
    this.moneyFormat = (this.dataset.moneyFormat || '${{amount}}').replace(/<[^>]*>/g, '');
    this.form = this.closest('form');

    // Escuchamos en el formulario porque el selector de cantidad es hermano,
    // no descendiente, de este componente.
    this.onQuantityUpdate = (event) => {
      const qty = event?.detail?.quantity;
      if (qty) this.#updatePrices(qty);
    };
    const scope = this.form || document;
    scope.addEventListener('quantity-selector:update', this.onQuantityUpdate);

    // Estado inicial según la cantidad ya presente en el input.
    this.#updatePrices(this.#currentQuantity());
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    const scope = this.form || document;
    scope.removeEventListener('quantity-selector:update', this.onQuantityUpdate);
  }

  /**
   * Lee la cantidad actual del input del formulario.
   * @returns {number}
   */
  #currentQuantity() {
    const input = /** @type {HTMLInputElement | null} */ (
      (this.form || document).querySelector('input[name="quantity"]')
    );
    return Math.max(1, parseInt(input?.value || '1', 10) || 1);
  }

  /**
   * Recalcula y pinta todos los montos en función de la cantidad.
   * @param {number} qty
   */
  #updatePrices(qty) {
    const quantity = Math.max(1, Number(qty) || 1);

    // Precios unitarios (compra única, suscripción y tachado) × cantidad.
    for (const el of this.querySelectorAll('[data-unit-price]')) {
      const unit = Number(el.getAttribute('data-unit-price')) || 0;
      el.textContent = this.#formatMoney(unit * quantity);
    }

    // Ahorro = descuento por unidad × cantidad + ahorro de envío (fijo por envío).
    for (const el of this.querySelectorAll('[data-discount-unit]')) {
      const discountUnit = Number(el.getAttribute('data-discount-unit')) || 0;
      const shipSave = Number(el.getAttribute('data-ship-save')) || 0;
      const total = discountUnit * quantity + shipSave;
      const label = el.getAttribute('data-savings-label') || '';
      const suffix = el.getAttribute('data-savings-suffix') || '';
      el.textContent = `${label} ${this.#formatMoney(total)} ${suffix}`.replace(/\s+/g, ' ').trim();
    }
  }

  /**
   * Formatea centavos al formato de moneda de la tienda (shop.money_format).
   * @param {number} cents
   * @returns {string}
   */
  #formatMoney(cents) {
    const format = this.moneyFormat;
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;

    const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
      if (isNaN(number) || number == null) return '0';
      const fixed = (number / 100.0).toFixed(precision);
      const parts = fixed.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      const rest = parts[1] ? decimal + parts[1] : '';
      return dollars + rest;
    };

    const match = format.match(placeholder);
    const token = match ? match[1] : 'amount';
    let value;
    switch (token) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', '');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ', '');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
    return format.replace(placeholder, value);
  }

  /**
   * Maneja el clic en una tarjeta.
   * @param {Event} event
   */
  select(event) {
    const target = event.target;
    const option = target instanceof Element ? target.closest('.goomy-sub__option') : null;
    if (!option) return;
    this.#activate(option);
  }

  /**
   * Marca una tarjeta como activa y sincroniza el plan elegido.
   * @param {Element} option
   */
  #activate(option) {
    for (const el of this.querySelectorAll('.goomy-sub__option')) {
      const isActive = el === option;
      el.classList.toggle('is-active', isActive);
      el.setAttribute('aria-checked', isActive ? 'true' : 'false');
    }

    const input = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="selling_plan"]'));
    if (input) {
      input.value = option.getAttribute('data-selling-plan') || '';
    }
  }
}

if (!customElements.get('goomy-subscription')) {
  customElements.define('goomy-subscription', GoomySubscription);
}
